/** The project (project) write model. */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { skillLink } from '../world/mutations'
import { writeStandingText } from '../world/standing'
import type { WorldSkill } from '../world/types'
import { changeProject } from './scalars'
import type { ProjectDoc } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any
/**
 * The project registry, HYDRATED, for a write.
 *
 * `repo.find` resolves as soon as the handle EXISTS, not when the document has
 * arrived. A `change` applied before then writes into a doc that reads as empty,
 * which is the "absent is not empty" shape with teeth: every registry write here
 * is guarded by `if (!d.sessionIds) d.sessionIds = []`, so on an unhydrated
 * handle it mints a fresh one-element list beside the real hundred-element one
 * rather than appending to it. When the real document lands, the registration is
 * simply gone.
 *
 * OBSERVED, not theorised: sessions created through a short-lived client were
 * missing from a 104-session registry while sessions created by the same
 * function after an explicit `whenReady` all landed. A session absent from the
 * registry is never hosted, never claimed and never drained, so it sits on
 * "waiting for a worker" forever while its own document looks perfectly healthy.
 *
 * Every write in this file goes through here for that reason. Reads already did
 * this individually; the writes are the half that can lose data.
 */
export async function findProjectForWrite(
  repo: Repo,
  projectId: string,
): Promise<DocHandle<ProjectDoc>> {
  const handle = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  return handle
}
/** Create an empty project doc (a session registry). Returns the handle so the
 *  caller has its `documentId`. Synchronous — `repo.create` is immediate. */
export function createProject(
  repo: Repo,
  opts?: { title?: string },
): DocHandle<ProjectDoc> {
  const handle = repo.create<ProjectDoc>()
  changeProject(handle, (d) => {
    d.title = opts?.title ?? 'Project'
    d.sessionIds = []
    d.sessionMeta = {}
    // A new project has no synthetic "Project files" mount. Legacy projects
    // without this field still read their old default source until migrated.
    d.sources = {}
  })
  return handle
}
/**
 * Add a skill to this project's catalog, or update the entry already there
 * (RFC 0334 3a). The World catalog's shape and semantics (`addWorldSkill`), on
 * the project: a directory entry pointing at a document that already exists.
 * Reaches agents in this project only, and replaces a World skill of the same
 * id here (`resolveSkills`).
 */
export async function addProjectSkill(
  repo: Repo,
  projectId: string,
  skillId: string,
  entry: {
    title: string
    description: string
    documentId: string
    sourceUpdatedAt?: number
  },
): Promise<void> {
  const handle = await findProjectForWrite(repo, projectId)
  const link = skillLink(entry.documentId)
  changeProject(handle, (d: Draft) => {
    if (!d.skills) d.skills = {}
    const existing = d.skills[skillId]
    if (existing) {
      // In place, for the reason `addWorldSkill` gives.
      existing.title = entry.title
      existing.description = entry.description
      existing.link = link
      if (entry.sourceUpdatedAt !== undefined)
        existing.sourceUpdatedAt = entry.sourceUpdatedAt
      return
    }
    d.skills[skillId] = {
      title: entry.title,
      description: entry.description,
      link,
      ...(entry.sourceUpdatedAt !== undefined
        ? { sourceUpdatedAt: entry.sourceUpdatedAt }
        : {}),
    } satisfies WorldSkill
  })
}
/**
 * Replace this project's standing instructions (RFC 0334 3b) wholesale, as
 * `setWorldAgentsMd` does for the World. '' clears them.
 */
export async function setProjectInstructions(
  repo: Repo,
  projectId: string,
  instructions: string,
): Promise<void> {
  // Into the instructions document once it has one (RFC 0334 3g).
  await writeStandingText(repo, 'project', projectId, instructions)
}
/** Drop a skill from this project's catalog. The document is untouched, and a
 *  World skill it shadowed is served here again. */
export async function removeProjectSkill(
  repo: Repo,
  projectId: string,
  skillId: string,
): Promise<boolean> {
  const handle = await findProjectForWrite(repo, projectId)
  let existed = false
  changeProject(handle, (d: Draft) => {
    if (d.skills?.[skillId]) {
      existed = true
      delete d.skills[skillId]
    }
  })
  return existed
}
